// useStore.js — Supabase-backed async data hook.
// Loads everything once on auth, then performs optimistic-ish updates.

import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import * as db from "../lib/db";
import { lookupBarcode, manualSku, newInventoryEntryForSku } from "../lib/pantry";
import { findDuplicateShoppingItem, bumpedQuantity } from "../lib/shop-mode";
import { resetExpiry, TRIAGE } from "../lib/reset-mode";
import { calculateAidenBrew } from "../lib/coffee";
import { applyTasteToBrew, EMPTY_TASTE_PROFILE, applyDeltaToProfile, checkInToBiasDelta } from "../lib/taste-profile";
import { cookEventToTasteDelta, applyDeltaToFoodProfile } from "../lib/food-taste-profile";
import { cupsToWaterMl, pickProfileForMode, decrementBeanWeight } from "../lib/brew-helpers";
import { supabase, uploadPhoto, getPhotoUrl, deletePhoto } from "../lib/supabase";
import {
  parseReceiptFromText,
  parseReceiptFromPhoto,
  parseCoffeeShipmentFromText,
  parseCoffeeShipmentFromPhoto,
  matchItemsToSkus,
} from "../lib/receipts";
import { parseRecipeInbox } from "../lib/recipe-inbox";
import { todayLocalDate } from "../lib/morning";
import { scheduleMedsPush, cancelScheduledPush } from "../lib/scheduled-push";
import { setActiveProvider, DEFAULT_PROVIDER } from "../lib/llm.js";
import { medsPet } from "../lib/household.js";
import { coffeeGear } from "../lib/coffee-gear.js";
import { fetchTripFeed, mergeFeedTrips, shouldSync } from "../lib/trip-feed.js";

const DEFAULT_PREFS = {
  servings: 2,
  units: "imperial",
  default_brew_water_ml: 500,
  default_brew_profile: "balanced",
  // 8 cups is the Aiden carafe favorite (see docs/coffee-handoff.md). Every coffee
  // surface reads this via `default_cups || 8`, so the dashboard cards and the
  // Morning Mode ritual now show the same dose. It used to be 3 here while Morning
  // Mode hardcoded 8 — a >2x dose disagreement between the two.
  default_cups: 8,
  trips: [],
};

// API key now lives in Supabase preferences (per-user, syncs across devices).
// localStorage is kept as a transitional cache so the key is available before
// the first preferences load completes, and as a fallback during migration.
const API_KEY_LS = "cucina_v1:api_key";
const CHECK_IN_DISMISS_LS = "cucina_v1:checkin_dismissed";

// Best-effort delete of an inbox row's orphaned Storage files. Called when the
// user DISCARDS a row (dismiss/delete) — NOT on accept/import, where a photo may
// be adopted by the created recipe/bean. Accepts a single path or an array;
// never throws, so a failed cleanup can't block the dismiss/delete.
async function cleanupInboxStorage(paths) {
  const list = (Array.isArray(paths) ? paths : [paths]).filter(Boolean);
  if (!list.length) return;
  await Promise.all(list.map(p =>
    deletePhoto(p).catch(e => console.warn("inbox storage cleanup failed", p, e?.message))
  ));
}

// The inbox paths NOT kept by the entity created on accept — so accept-time
// cleanup can never delete a photo the new recipe/bean/sku actually adopted.
// (Verified today none of the accept flows adopt the inbox photo, but this keeps
// it safe if that ever changes.)
function orphanedPaths(paths, ...keptPaths) {
  const kept = new Set(keptPaths.filter(Boolean));
  return (Array.isArray(paths) ? paths : [paths]).filter(p => p && !kept.has(p));
}

export function useStore({ userId, ready }) {

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [appliances, setAppliances] = useState([]);
  const [skus, setSkus] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [beans, setBeans] = useState([]);
  const [brewLogs, setBrewLogs] = useState([]);
  const [morningSession, setMorningSession] = useState(null);
  const [preferences, setPrefsState] = useState(DEFAULT_PREFS);
  const [apiKey, setApiKeyState] = useState(() => localStorage.getItem(API_KEY_LS) || "");
  // Which LLM provider is active, and one key per provider. `apiKey` above stays
  // the ACTIVE provider's key so existing call sites keep working unchanged.
  const [aiProvider, setAiProviderState] = useState(DEFAULT_PROVIDER);
  const [aiKeys, setAiKeys] = useState({});

  // Learning loop state
  const [tasteProfile, setTasteProfile] = useState(EMPTY_TASTE_PROFILE);
  const [favorites, setFavorites] = useState([]);
  const [checkIns, setCheckIns] = useState([]);
  const [shoppingList, setShoppingList] = useState([]);
  const [bottomlessImports, setBottomlessImports] = useState([]);
  const [cookEvents, setCookEvents] = useState([]);
  const [pantryInbox, setPantryInbox] = useState([]);
  const [coffeeInbox, setCoffeeInbox] = useState([]);
  const [recipeInbox, setRecipeInbox] = useState([]);

  // Always-current mirrors so callbacks can dedupe/lookup without stale closures
  // or churning their dep arrays (used by the shopping dedupe + depletion feeder).
  const shoppingListRef = useRef([]);
  shoppingListRef.current = shoppingList;
  const skusRef = useRef([]);
  skusRef.current = skus;
  // Read inside setApiKey/setAiProvider without adding them to dep arrays,
  // which would rebuild those callbacks on every keystroke.
  const aiProviderRef = useRef(DEFAULT_PROVIDER);
  aiProviderRef.current = aiProvider;
  const aiKeysRef = useRef({});
  aiKeysRef.current = aiKeys;

  // Save the key for the ACTIVE provider. Kept named setApiKey because ~95 call
  // sites already use store.apiKey / store.setApiKey; only the storage behind it
  // widened from one key to one per provider.
  // Refresh trips from the user's calendar feed, at most every few hours.
  // Fire-and-forget on purpose: this is a nice-to-have enrichment, so a slow or
  // broken third-party feed must never delay startup or surface an error the
  // user did not ask for. Failures are logged and retried on the next load.
  useEffect(() => {
    const url = preferences?.trip_feed_url;
    if (!userId || !url) return;
    if (!shouldSync(preferences?.trip_feed_synced_at)) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchTripFeed(url);
        if (cancelled) return;
        const merged = mergeFeedTrips(preferences.trips || [], data.trips);
        await db.upsertPreferences(userId, {
          trips: merged,
          trip_feed_synced_at: new Date().toISOString(),
        });
        setPrefsState(p => ({ ...p, trips: merged, trip_feed_synced_at: new Date().toISOString() }));
      } catch (e) {
        console.warn("trip feed sync failed", e?.message);
      }
    })();
    return () => { cancelled = true; };
    // Keyed on the URL and last-sync stamp only — re-running on every
    // preferences change would defeat the throttle.
  }, [userId, preferences?.trip_feed_url, preferences?.trip_feed_synced_at]);

  // What the user actually brews and grinds with. Drives whether the Aiden
  // profile UI and the Ode's 1-11 scale apply at all — see lib/coffee-gear.js.
  const gear = useMemo(() => coffeeGear(appliances, preferences), [appliances, preferences]);

  const setApiKey = useCallback(async (k) => {
    setApiKeyState(k);
    if (k) localStorage.setItem(API_KEY_LS, k);
    else localStorage.removeItem(API_KEY_LS);
    if (userId) {
      try {
        const nextKeys = { ...(aiKeysRef.current || {}), [aiProviderRef.current]: k || null };
        await db.upsertPreferences(userId, {
          ai_keys: nextKeys,
          ai_provider: aiProviderRef.current,
          ai_api_key_present: !!k,
          // Mirror to the legacy column while the active provider is Anthropic,
          // so a stale tab still running the old build keeps working. The
          // migration that drops that column also drops this line.
          ...(aiProviderRef.current === "anthropic" ? { ai_api_key: k || null } : {}),
        });
        setAiKeys(nextKeys);
      } catch (e) {
        console.warn("api key sync failed", e);
      }
    }
  }, [userId]);

  // Switch providers. The key swaps with it, so the app is immediately talking
  // to the new provider with that provider's own credentials.
  const setAiProvider = useCallback(async (provider, { baseUrl } = {}) => {
    const nextKeys = baseUrl != null
      ? { ...(aiKeysRef.current || {}), compatible_base_url: baseUrl }
      : (aiKeysRef.current || {});
    setAiProviderState(provider);
    setAiKeys(nextKeys);
    setActiveProvider({ provider, baseUrl: nextKeys.compatible_base_url });
    const key = nextKeys[provider] || "";
    setApiKeyState(key);
    if (key) localStorage.setItem(API_KEY_LS, key); else localStorage.removeItem(API_KEY_LS);
    if (userId) {
      try {
        await db.upsertPreferences(userId, { ai_provider: provider, ai_keys: nextKeys });
      } catch (e) {
        console.warn("provider sync failed", e);
      }
    }
  }, [userId]);

  // Initial bulk load on user change
  useEffect(() => {
    if (!userId) {
      setAppliances([]); setSkus([]); setInventory([]);
      setRecipes([]); setBeans([]); setBrewLogs([]);
      setTasteProfile(EMPTY_TASTE_PROFILE);
      setFavorites([]); setCheckIns([]); setShoppingList([]);
      setBottomlessImports([]); setCookEvents([]);
      setPantryInbox([]); setCoffeeInbox([]); setRecipeInbox([]);
      setMorningSession(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const [a, s, i, r, b, bl, p, fav, ci, tp, sl, bi, ce, pi, co, ri] = await Promise.all([
          db.listAppliances(userId),
          db.listSkus(userId),
          db.listInventory(userId),
          db.listRecipes(userId),
          db.listBeans(userId),
          db.listBrewLogs(userId, 100),
          db.getPreferences(userId),
          db.listCoffeeFavorites(userId).catch(() => []),
          db.listCheckIns(userId, 50).catch(() => []),
          db.getTasteProfile(userId).catch(() => null),
          db.listShoppingItems(userId, false).catch(() => []),
          db.listBottomlessImports(userId, "pending").catch(() => []),
          db.listCookEvents(userId, null, 100).catch(() => []),
          db.listPantryInbox(userId).catch(() => []),
          db.listCoffeeInbox(userId).catch(() => []),
          db.listRecipeInbox(userId).catch(() => []),
        ]);
        // Today's Morning Mode session (may not exist yet). Loaded after the
        // main batch so a schema without the table can't break the whole app.
        const ms = await db.getMorningSession(userId, todayLocalDate()).catch(() => null);
        if (cancelled) return;
        setAppliances(a || []);
        setSkus(s || []);
        setInventory(i || []);
        setRecipes(r || []);
        setBeans(b || []);
        setBrewLogs(bl || []);
        setPrefsState({ ...DEFAULT_PREFS, ...(p || {}) });
        // Hydrate provider + keys. Three sources, oldest last:
        //   1. ai_keys      — per-provider map (current)
        //   2. ai_api_key   — the legacy single Anthropic key
        //   3. localStorage — device-only, from before keys synced at all
        const provider = p?.ai_provider || DEFAULT_PROVIDER;
        const keys = { ...(p?.ai_keys || {}) };
        const legacyKey = p?.ai_api_key || "";
        const localKey = localStorage.getItem(API_KEY_LS) || "";

        // Fold the legacy key forward so nobody has to re-paste. The SQL
        // migration does this too; this covers a row written by an older client
        // after that migration ran.
        let needsWrite = false;
        if (!keys.anthropic && legacyKey) { keys.anthropic = legacyKey; needsWrite = true; }
        if (!keys.anthropic && !legacyKey && localKey) { keys.anthropic = localKey; needsWrite = true; }

        setAiProviderState(provider);
        setAiKeys(keys);
        setActiveProvider({ provider, baseUrl: keys.compatible_base_url });

        const activeKey = keys[provider] || "";
        setApiKeyState(activeKey);
        if (activeKey) localStorage.setItem(API_KEY_LS, activeKey);
        else localStorage.removeItem(API_KEY_LS);

        if (needsWrite) {
          db.upsertPreferences(userId, { ai_keys: keys, ai_provider: provider, ai_api_key_present: true })
            .catch(e => console.warn("api key migration failed", e));
        }
        setFavorites(fav || []);
        setCheckIns(ci || []);
        setTasteProfile(tp || EMPTY_TASTE_PROFILE);
        setShoppingList(sl || []);
        setBottomlessImports(bi || []);
        setCookEvents(ce || []);
        setPantryInbox(pi || []);
        setCoffeeInbox(co || []);
        setRecipeInbox(ri || []);
        setMorningSession(ms || null);
      } catch (e) {
        console.error(e);
        setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // Listen for AI usage events fired by vision.js / ai.js and write them
  // to ai_usage_logs. Best-effort — failures don't block the AI feature.
  useEffect(() => {
    if (!userId) return;
    const handler = (e) => {
      const { kind, model, usage } = e.detail || {};
      if (!usage) return;
      const inp = +(usage.input_tokens || 0);
      const out = +(usage.output_tokens || 0);
      const cr = +(usage.cache_read_input_tokens || 0);
      const cc = +(usage.cache_creation_input_tokens || 0);
      // Sonnet 4.6 pricing: $3 input / $15 output / $0.30 cache-read /
      // $3.75 cache-create per 1M tokens. Convert to cents.
      const cost = (inp * 0.0003) + (out * 0.0015) + (cr * 0.00003) + (cc * 0.000375);
      db.logAiUsage(userId, {
        kind: kind || "unknown",
        model: model || null,
        input_tokens: inp,
        output_tokens: out,
        cache_read_tokens: cr,
        cache_creation_tokens: cc,
        estimated_cost_cents: +cost.toFixed(3),
      });
    };
    window.addEventListener("cucina-ai-usage", handler);
    return () => window.removeEventListener("cucina-ai-usage", handler);
  }, [userId]);

  // Derived
  const ownedCapabilities = useMemo(() => {
    const caps = new Set();
    for (const a of appliances) {
      (a.capabilities || []).forEach(c => caps.add(c));
    }
    return caps;
  }, [appliances]);

  // Helper: upload a photo file to storage, returns the storage path
  const uploadEntityPhoto = useCallback(async (file, entity) => {
    if (!userId) throw new Error("Not signed in");
    return uploadPhoto(file, entity, userId);
  }, [userId]);

  // ---- Appliances ----
  const addAppliance = useCallback(async (payload) => {
    const created = await db.addAppliance(userId, payload);
    setAppliances(prev => [...prev, created]);
    return created;
  }, [userId]);

  const updateApplianceRow = useCallback(async (id, updates) => {
    const updated = await db.updateAppliance(id, updates);
    setAppliances(prev => prev.map(a => a.id === id ? updated : a));
    return updated;
  }, []);

  const removeAppliance = useCallback(async (id) => {
    await db.deleteAppliance(id);
    setAppliances(prev => prev.filter(a => a.id !== id));
  }, []);

  // Toggle a library appliance (e.g. "fellow_aiden") on/off.
  // Insert a row if turning on, delete the row if turning off.
  const toggleLibraryAppliance = useCallback(async (lib) => {
    const existing = appliances.find(a => a.library_id === lib.id);
    if (existing) {
      await removeAppliance(existing.id);
    } else {
      await addAppliance({
        library_id: lib.id,
        name: lib.name,
        category: lib.category,
        capabilities: lib.capabilities,
      });
    }
  }, [appliances, addAppliance, removeAppliance]);

  // ---- Pantry ----
  const scanBarcode = useCallback(async (barcode, quantity = 1) => {
    let sku = await db.findSkuByBarcode(userId, barcode);
    if (!sku) {
      const off = await lookupBarcode(barcode);
      if (!off) return { ok: false, reason: "not_found", barcode };
      sku = await db.upsertSku(userId, { ...off, user_id: undefined });
      setSkus(prev => prev.find(s => s.id === sku.id) ? prev : [...prev, sku]);
    }
    const entry = await db.addInventoryEntry(userId, newInventoryEntryForSku(sku, quantity));
    setInventory(prev => [...prev, entry]);
    return { ok: true, sku, entry };
  }, [userId]);

  const addManualPantryItem = useCallback(async ({ name, brand = "", category = "pantry", barcode = null, photo_path = null, details = {} }, quantity = 1) => {
    const skuPayload = manualSku({ name, brand, category, barcode });
    const sku = await db.upsertSku(userId, { ...skuPayload, photo_path, details });
    setSkus(prev => prev.find(s => s.id === sku.id) ? prev : [...prev, sku]);
    const entry = await db.addInventoryEntry(userId, newInventoryEntryForSku(sku, quantity));
    setInventory(prev => [...prev, entry]);
    return { sku, entry };
  }, [userId]);

  // Reset Mode — commit one triaged item from a bulk pantry-audit session.
  // Keep/Use-soon become a pantry_inventory row (session location + qty + a
  // triage-aware expiry, see resetExpiry); Toss becomes a shopping_list replace
  // item and is NOT kept. `item` is the UI-side shape from src/modules/Reset.jsx:
  //   { triage, name, brand, category, barcode, qty, location, bestBy, meta, skuPayload? }
  // where skuPayload is the /api/barcode-resolved DB-shaped sku when we have one.
  const commitResetItem = useCallback(async (item) => {
    if (!userId) throw new Error("Not signed in");
    const qty = Math.max(1, item.qty || 1);

    if (item.triage === TRIAGE.TOSS) {
      const row = await db.addShoppingItem(userId, {
        text: item.name,
        sku_id: null,
        quantity: qty > 1 ? String(qty) : null,
        source: "reset-toss",
      });
      setShoppingList(prev => [row, ...prev]);
      return { triage: TRIAGE.TOSS, shopping: row };
    }

    // Keep / Use soon → ensure a sku, then insert inventory.
    let sku = item.barcode ? await db.findSkuByBarcode(userId, item.barcode) : null;
    if (!sku) {
      const payload = item.skuPayload
        ? { ...item.skuPayload, canonical_name: item.name || item.skuPayload.canonical_name, category: item.category || item.skuPayload.category }
        : manualSku({ name: item.name, brand: item.brand || "", category: item.category || "pantry", barcode: item.barcode || null });
      sku = await db.upsertSku(userId, payload);
      setSkus(prev => prev.find(s => s.id === sku.id) ? prev : [...prev, sku]);
    }

    const base = newInventoryEntryForSku(sku, qty);
    const expires = resetExpiry({ triage: item.triage, bestBy: item.bestBy, category: sku.category });
    const entry = await db.addInventoryEntry(userId, {
      ...base,
      location: item.location || base.location,
      expires_at: expires || base.expires_at,
    });
    setInventory(prev => [...prev, entry]);
    return { triage: item.triage, sku, entry };
  }, [userId]);

  const removeInventoryEntry = useCallback(async (id) => {
    await db.deleteInventoryEntry(id);
    setInventory(prev => prev.filter(e => e.id !== id));
  }, []);

  const updateInventoryEntry = useCallback(async (id, updates) => {
    const updated = await db.updateInventoryEntry(id, updates);
    setInventory(prev => prev.map(e => e.id === id ? updated : e));
    return updated;
  }, []);

  // Decrement quantity by `amount`. If quantity hits 0, delete the entry.
  const useInventoryAmount = useCallback(async (id, amount) => {
    const entry = inventory.find(e => e.id === id);
    if (!entry) return;
    const newQty = Math.max(0, +(entry.quantity || 0) - +(amount || 0));
    if (newQty <= 0) {
      await db.deleteInventoryEntry(id);
      setInventory(prev => prev.filter(e => e.id !== id));
      // Depletion feeder (§H v1): if this was the LAST stock of its sku, self-add
      // it to the shopping list so the list builds itself as you run out. Guarded
      // — a shopping write must never break the pantry decrement. Deduped so it
      // adds at most one line; hit-zero is a one-time event, so no re-nag.
      try {
        const stillStocked = inventory.some(e => e.id !== id && e.sku_id === entry.sku_id);
        if (entry.sku_id && !stillStocked) {
          const sku = skusRef.current.find(s => s.id === entry.sku_id);
          if (sku && !findDuplicateShoppingItem(shoppingListRef.current, { sku_id: sku.id, text: sku.canonical_name })) {
            const row = await db.addShoppingItem(userId, { text: sku.canonical_name, sku_id: sku.id, quantity: null, source: "depleted" });
            setShoppingList(prev => [row, ...prev]);
          }
        }
      } catch (e) { console.warn("depletion feeder failed", e?.message); }
    } else {
      const updated = await db.updateInventoryEntry(id, { quantity: newQty });
      setInventory(prev => prev.map(e => e.id === id ? updated : e));
    }
  }, [inventory, userId]);

  const removeSku = useCallback(async (id) => {
    await db.deleteSku(id);
    setSkus(prev => prev.filter(s => s.id !== id));
    setInventory(prev => prev.filter(e => e.sku_id !== id));
  }, []);

  // Set (or clear, with null) a sku's min-keep par level (Shop Mode v2). When the
  // pantry total dips below this, Shopping surfaces it in the "Running low" tray.
  const setSkuMinKeep = useCallback(async (id, minKeep) => {
    const value = minKeep == null || minKeep === "" ? null : Math.max(0, +minKeep);
    const updated = await db.updateSku(id, { min_keep: value });
    setSkus(prev => prev.map(s => s.id === id ? updated : s));
    return updated;
  }, []);

  // ---- Recipes ----
  const addRecipeRow = useCallback(async (payload) => {
    const flat = flattenRecipe(payload);
    const created = await db.addRecipe(userId, flat);
    setRecipes(prev => [created, ...prev]);
    return created;
  }, [userId]);

  const updateRecipeRow = useCallback(async (id, payload) => {
    const flat = flattenRecipe(payload);
    const updated = await db.updateRecipe(id, flat);
    setRecipes(prev => prev.map(r => r.id === id ? updated : r));
    return updated;
  }, []);

  const removeRecipe = useCallback(async (id) => {
    await db.deleteRecipe(id);
    setRecipes(prev => prev.filter(r => r.id !== id));
  }, []);

  // Public share link: mint an unguessable token (reusing an existing one) and
  // return the /api/shared-recipe URL. Writes share_token directly (not through
  // flattenRecipe, which would drop the unknown field).
  const createRecipeShareLink = useCallback(async (id) => {
    const recipe = recipes.find(r => r.id === id);
    let token = recipe?.share_token;
    if (!token) {
      token = crypto.randomUUID().replace(/-/g, "");
      const updated = await db.updateRecipe(id, { share_token: token });
      setRecipes(prev => prev.map(r => r.id === id ? updated : r));
    }
    return `${window.location.origin}/api/shared-recipe?token=${token}`;
  }, [recipes]);

  // Revoke a share link — the public URL 404s afterward.
  const revokeRecipeShareLink = useCallback(async (id) => {
    const updated = await db.updateRecipe(id, { share_token: null });
    setRecipes(prev => prev.map(r => r.id === id ? updated : r));
  }, []);

  const markMade = useCallback(async (id) => {
    const r = recipes.find(x => x.id === id);
    const updated = await db.markRecipeMade(id, r?.times_made || 0);
    setRecipes(prev => prev.map(x => x.id === id ? updated : x));
  }, [recipes]);

  const toggleFavorite = useCallback(async (id) => {
    const r = recipes.find(x => x.id === id);
    if (!r) return;
    const updated = await db.updateRecipe(id, { favorite: !r.favorite });
    setRecipes(prev => prev.map(x => x.id === id ? updated : x));
  }, [recipes]);

  // Reorder the user's favorite recipes. orderedIds is the list of
  // favorited recipe ids in the new display order.
  const reorderFavoriteRecipesList = useCallback(async (orderedIds) => {
    if (!userId) return;
    setRecipes(prev => {
      const idx = new Map(orderedIds.map((id, i) => [id, i]));
      return prev.map(r => idx.has(r.id) ? { ...r, favorite_order: idx.get(r.id) } : r);
    });
    try {
      await db.reorderFavoriteRecipes(userId, orderedIds);
    } catch (e) {
      console.warn("reorderFavoriteRecipes failed", e);
    }
  }, [userId]);

  // Bulk import: accepts an array of recipe objects (any shape flattenRecipe
  // accepts), inserts them sequentially. Yields a result object with counts
  // and per-row outcomes so the UI can show progress.
  // Optional opts.onProgress(index, total, lastResult) for live updates.
  // Optional opts.mode = "skip" | "replace" | "duplicate":
  //   - "skip" (default): skip rows whose title matches an existing recipe
  //   - "replace": overwrite existing recipes when titles match (preserves
  //                favorite, times_made, last_made_at, photo_path on the
  //                existing row; replaces ingredients/steps/etc. from import)
  //   - "duplicate": insert every row as a new recipe even if the title exists
  // (Legacy opts.dedupe = true/false maps to "skip"/"duplicate" for back-compat.)
  const bulkImportRecipes = useCallback(async (rows, opts = {}) => {
    const out = { created: [], updated: [], skipped: [], failed: [] };
    const mode = opts.mode || (opts.dedupe === false ? "duplicate" : "skip");

    // Map normalized title → existing recipe (for replace mode lookups)
    const titleToExisting = new Map();
    (recipes || []).forEach(r => {
      if (r.title) titleToExisting.set(normalizeTitle(r.title), r);
    });

    const total = rows.length;

    for (let i = 0; i < total; i++) {
      const row = rows[i];
      try {
        const flat = flattenRecipe(row);
        if (!flat?.title?.trim()) {
          out.failed.push({ row, reason: "missing title" });
        } else {
          // Apply optional batch tags (from "Mom's recipe box", etc.)
          const tagged = opts.sourceTag ? {
            ...flat,
            source_ref: flat.source_ref || opts.sourceTag,
            source_type: flat.source_type || "import",
            cuisine: dedupTags([...(flat.cuisine || []), ...(opts.cuisineTags || [])]),
            mood_tags: dedupTags([...(flat.mood_tags || []), ...(opts.moodTags || [])]),
          } : flat;

          const titleKey = normalizeTitle(flat.title);
          const existing = titleToExisting.get(titleKey);

          if (existing && mode === "skip") {
            out.skipped.push({ title: flat.title, reason: "duplicate title" });
          } else if (existing && mode === "replace") {
            // Preserve user-curated runtime fields on the existing row.
            const patch = {
              ...tagged,
              favorite: existing.favorite ?? tagged.favorite,
              times_made: existing.times_made ?? tagged.times_made,
              last_made_at: existing.last_made_at ?? tagged.last_made_at,
              photo_path: existing.photo_path ?? tagged.photo_path,
            };
            const updated = await db.updateRecipe(existing.id, patch);
            out.updated.push(updated);
            titleToExisting.set(titleKey, updated);
          } else {
            // Either no existing match, or mode === "duplicate"
            const created = await db.addRecipe(userId, tagged);
            out.created.push(created);
            titleToExisting.set(titleKey, created);
          }
        }
      } catch (e) {
        out.failed.push({ row, reason: e?.message || "unknown error" });
      }
      if (opts.onProgress) opts.onProgress(i + 1, total, out);
    }

    // Refresh state: prepend new + replace updated in place.
    if (out.created.length || out.updated.length) {
      setRecipes(prev => {
        const updatedById = new Map(out.updated.map(r => [r.id, r]));
        const merged = prev.map(r => updatedById.get(r.id) || r);
        return [...out.created, ...merged];
      });
    }
    return out;
  }, [userId, recipes]);

  // ---- Beans ----
  const addBeanRow = useCallback(async (payload) => {
    const created = await db.addBean(userId, payload);
    setBeans(prev => [created, ...prev]);
    return created;
  }, [userId]);

  const updateBeanRow = useCallback(async (id, updates) => {
    const updated = await db.updateBean(id, updates);
    setBeans(prev => prev.map(b => b.id === id ? updated : b));
    return updated;
  }, []);

  const removeBean = useCallback(async (id) => {
    await db.deleteBean(id);
    setBeans(prev => prev.filter(b => b.id !== id));
  }, []);

  // Reorder beans on the Coffee tab. orderedIds is the new display order.
  // Optimistically updates local state, then persists sort_order to the DB.
  const reorderBeansList = useCallback(async (orderedIds) => {
    if (!userId) return;
    setBeans(prev => {
      const byId = new Map(prev.map(b => [b.id, b]));
      const next = orderedIds
        .map((id, idx) => {
          const b = byId.get(id);
          return b ? { ...b, sort_order: idx } : null;
        })
        .filter(Boolean);
      // Append any beans not in orderedIds (defensive — shouldn't happen)
      for (const b of prev) {
        if (!orderedIds.includes(b.id)) next.push(b);
      }
      return next;
    });
    try {
      await db.reorderBeans(userId, orderedIds);
    } catch (e) {
      console.warn("reorderBeans failed", e);
    }
  }, [userId]);

  // ---- Coffee compute + log (taste-aware) ----
  // computeBrew now accepts {beanId, profileId?, waterMl?, cups?, mode?} and applies taste bias.
  const computeBrew = useCallback((opts) => {
    // Backwards compat: positional args (beanId, profileId, waterMl)
    let beanId, profileId, waterMl, cups, mode, suggestedProfileId;
    if (typeof opts === "string") {
      beanId = opts;
      profileId = arguments[1];
      waterMl = arguments[2];
    } else {
      ({ beanId, profileId, waterMl, cups, mode, suggestedProfileId } = opts || {});
    }

    const bean = beans.find(b => b.id === beanId);
    if (!bean) return null;

    // Resolve water from cups if cups was passed
    if (cups && !waterMl) waterMl = cupsToWaterMl(cups);

    // If a brew mode was passed, pick profile based on it
    if (mode) {
      profileId = pickProfileForMode({
        mode,
        suggestedProfileId,
        fallback: profileId || preferences.default_brew_profile,
      });
    }

    const baseBrew = calculateAidenBrew({
      bean: {
        id: bean.id,
        name: bean.name,
        roast_level: bean.roast_level,
        process: bean.process,
        roast_date: bean.roast_date,
      },
      profileId: profileId || preferences.default_brew_profile,
      waterMl: waterMl || preferences.default_brew_water_ml,
          grinder: gear.grinder,
    });

    // Bias by taste profile (this is the learning loop closing)
    return applyTasteToBrew(baseBrew, tasteProfile, gear.grinder);
  }, [beans, preferences, tasteProfile, gear]);

  const logBrew = useCallback(async (brew, feedback = "", rating = null) => {
    const log = await db.addBrewLog(userId, {
      bean_id: brew.bean_id,
      profile_id: brew.profile?.id,
      dose_g: brew.dose_g,
      water_g: brew.water_g,
      grind_setting: brew.grind?.setting,
      temp_c: brew.temp_c,
      brew_data: brew,
      feedback,
      rating,
      // Morning Mode provenance (nullable for legacy/manual brews).
      source: brew.source || null,
      rationale: brew.rationale || null,
    });
    setBrewLogs(prev => [log, ...prev]);

    // The bag you just brewed is now the one in the container. Persist it so
    // every coffee surface (Today, Daily, Morning Mode, the canister card) agrees
    // via canisterBean(beans, preferences). Without this, only the Coffee-tab
    // picker set last_bean_id — so brewing a new bag through Morning Mode left the
    // dashboard still pointing at the previous bag. This completes setContainerBean.
    if (brew.bean_id) {
      try {
        const prevPrefs = preferences || {};
        const patch = { ...prevPrefs, last_bean_id: brew.bean_id };
        if (Array.isArray(prevPrefs.open_bean_ids) && !prevPrefs.open_bean_ids.includes(brew.bean_id)) {
          patch.open_bean_ids = [...prevPrefs.open_bean_ids, brew.bean_id];
        }
        if (prevPrefs.last_bean_id !== brew.bean_id || patch.open_bean_ids !== prevPrefs.open_bean_ids) {
          const saved = await db.upsertPreferences(userId, patch);
          setPrefsState({ ...DEFAULT_PREFS, ...(saved || patch) });
        }
      } catch (e) { console.warn("persist container bean failed", e?.message); }
    }

    // Decrement bean weight remaining; retire the bag when it hits ~0.
    const bean = beans.find(b => b.id === brew.bean_id);
    if (bean) {
      const updated = decrementBeanWeight(bean, brew.dose_g);
      const beanUpdates = { weight_left_g: updated.weight_left_g };
      if (updated.weight_left_g <= 2 && bean.status !== "finished") beanUpdates.status = "finished";
      await db.updateBean(bean.id, beanUpdates);
      setBeans(prev => prev.map(b => b.id === bean.id ? { ...updated, ...beanUpdates } : b));
    }

    return log;
  }, [userId, beans, preferences]);

  // Blend "Started brew" — one-off recipe over TWO bags. Decrements EACH bag by
  // its ratio share (from blend_components), retiring any bag that crosses ~0.
  // Deliberately writes NO calibration history to either bag (bean_id null; the
  // rating loop is skipped upstream) — the blend is labeled one-off per the spec.
  // components: [{ bean_id, grams }, ...].
  const logBlendBrew = useCallback(async (brew, components) => {
    const parts = (components || []).filter(c => c && c.bean_id && +c.grams > 0);
    const totalDose = parts.reduce((s, c) => s + +c.grams, 0);
    const log = await db.addBrewLog(userId, {
      bean_id: null,
      profile_id: brew.profile?.id,
      dose_g: totalDose,
      water_g: brew.water_g,
      grind_setting: brew.grind?.setting,
      temp_c: brew.temp_c,
      brew_data: brew,
      source: "blend",
      rationale: brew.rationale || null,
      blend_components: parts,
    });
    setBrewLogs(prev => [log, ...prev]);

    for (const c of parts) {
      const bean = beans.find(b => b.id === c.bean_id);
      if (!bean) continue;
      const newLeft = Math.max(0, +(bean.weight_left_g ?? bean.weight_g ?? 0) - +c.grams);
      const updates = { weight_left_g: newLeft };
      if (newLeft <= 2 && bean.status !== "finished") updates.status = "finished";
      await db.updateBean(bean.id, updates);
      setBeans(prev => prev.map(b => b.id === bean.id ? { ...b, ...updates } : b));
    }
    return log;
  }, [userId, beans]);

  // Update an existing brew log. If dose_g changed, adjust the bean's
  // weight_left_g by the delta so the depletion tracker stays accurate.
  const updateBrewLog = useCallback(async (id, updates) => {
    const prev = brewLogs.find(l => l.id === id);
    const updated = await db.updateBrewLog(id, updates);
    setBrewLogs(p => p.map(l => l.id === id ? updated : l));
    if (prev && updates.dose_g != null && +updates.dose_g !== +prev.dose_g) {
      const delta = +prev.dose_g - +updates.dose_g; // positive = bean got back grams
      const bean = beans.find(b => b.id === prev.bean_id);
      if (bean && bean.weight_left_g != null) {
        const newLeft = Math.max(0, Math.min(+(bean.weight_g ?? 99999), +bean.weight_left_g + delta));
        await db.updateBean(bean.id, { weight_left_g: newLeft });
        setBeans(p => p.map(b => b.id === bean.id ? { ...b, weight_left_g: newLeft } : b));
      }
    }
    return updated;
  }, [brewLogs, beans]);

  // Delete a brew log. Restore the dose to the bean's weight_left_g.
  const deleteBrewLog = useCallback(async (id) => {
    const prev = brewLogs.find(l => l.id === id);
    await db.deleteBrewLog(id);
    setBrewLogs(p => p.filter(l => l.id !== id));
    if (prev && prev.dose_g) {
      const bean = beans.find(b => b.id === prev.bean_id);
      if (bean && bean.weight_left_g != null) {
        const newLeft = Math.min(+(bean.weight_g ?? 99999), +bean.weight_left_g + +prev.dose_g);
        await db.updateBean(bean.id, { weight_left_g: newLeft });
        setBeans(p => p.map(b => b.id === bean.id ? { ...b, weight_left_g: newLeft } : b));
      }
    }
  }, [brewLogs, beans]);

  // ---- Favorites ----
  const saveFavorite = useCallback(async ({ name, brew, beanId, isDefault = false }) => {
    if (!userId) throw new Error("Not signed in");
    // If this is set as default, clear others first
    if (isDefault && beanId) {
      const others = favorites.filter(f => f.bean_id === beanId && f.is_default_for_bean);
      for (const o of others) {
        await db.upsertFavoriteDefault(o.id, false);
      }
    }
    const fav = await db.addCoffeeFavorite(userId, {
      bean_id: beanId,
      name,
      profile_id: brew.profile?.id || "balanced",
      aiden_profile: brew.aidenProfile,
      water_ml: brew.water_g,
      grind_setting: brew.grind?.setting,
      is_default_for_bean: isDefault,
    });
    setFavorites(prev => [fav, ...prev]);
    return fav;
  }, [userId, favorites]);

  const useFavorite = useCallback(async (favId) => {
    const fav = await db.bumpCoffeeFavorite(favId);
    setFavorites(prev => prev.map(f => f.id === favId ? fav : f));
    return fav;
  }, []);

  const removeFavorite = useCallback(async (favId) => {
    await db.deleteCoffeeFavorite(favId);
    setFavorites(prev => prev.filter(f => f.id !== favId));
  }, []);

  // ---- Morning Mode sessions ----
  // Create-or-fetch today's session row so the step UI + timer chip have durable
  // state. Idempotent — safe to call on every Morning Mode mount.
  const startMorningSession = useCallback(async () => {
    if (!userId) throw new Error("Not signed in");
    const row = await db.ensureMorningSession(userId, todayLocalDate());
    setMorningSession(row);
    return row;
  }, [userId]);

  // Mark Mackenzie's meds given → stamp meds_given_at (the countdown derives from
  // this), and schedule the server-side +Nmin completion push. The push id is
  // stored so undoing the step can cancel it. Best-effort scheduling: a failed
  // push insert must not block the check-off.
  const setMedsGiven = useCallback(async (beanName = "") => {
    const session = morningSession || await db.ensureMorningSession(userId, todayLocalDate());
    const givenAt = new Date().toISOString();
    const mins = Number.isFinite(+preferences?.meds_timer_minutes) ? +preferences.meds_timer_minutes : 60;
    const fireAt = new Date(Date.now() + mins * 60 * 1000).toISOString();
    let pushId = null;
    try {
      pushId = await scheduleMedsPush({
        supabase, userId, sessionId: session.id, beanName, fireAt,
        pet: medsPet(preferences),
      });
    } catch (e) { console.warn("meds push schedule failed", e?.message); }
    const row = await db.updateMorningSession(session.id, { meds_given_at: givenAt, meds_push_id: pushId });
    setMorningSession(row);
    return row;
  }, [userId, morningSession, preferences]);

  // Undo "meds given" — clear the timestamp and cancel the scheduled push.
  const clearMedsGiven = useCallback(async () => {
    if (!morningSession) return;
    if (morningSession.meds_push_id) {
      try {
        await cancelScheduledPush({ supabase, id: morningSession.meds_push_id });
      } catch (e) { console.warn("meds push cancel failed", e?.message); }
    }
    const row = await db.updateMorningSession(morningSession.id, { meds_given_at: null, meds_push_id: null });
    setMorningSession(row);
    return row;
  }, [morningSession]);

  // Toggle a prep check-off (split_dose / water / filter) in the prep jsonb.
  const setPrepStep = useCallback(async (key, value) => {
    const session = morningSession || await db.ensureMorningSession(userId, todayLocalDate());
    const prep = { ...(session.prep || {}), [key]: value };
    const row = await db.updateMorningSession(session.id, { prep });
    setMorningSession(row);
    return row;
  }, [userId, morningSession]);

  // Finish the morning — link the logged brew and stamp completed_at so re-opening
  // in the window lands on normal home instead of the finished flow.
  const completeMorning = useCallback(async (brewLogId = null) => {
    const session = morningSession || await db.ensureMorningSession(userId, todayLocalDate());
    const row = await db.updateMorningSession(session.id, {
      brew_log_id: brewLogId ?? session.brew_log_id ?? null,
      completed_at: new Date().toISOString(),
    });
    setMorningSession(row);
    return row;
  }, [userId, morningSession]);

  // ---- Check-ins ----
  const submitCheckIn = useCallback(async (payload) => {
    if (!userId) throw new Error("Not signed in");
    const ci = await db.addCheckIn(userId, { ...payload, applied_to_profile: true });
    setCheckIns(prev => [ci, ...prev]);

    // Update taste profile from this check-in
    const delta = checkInToBiasDelta(payload);
    const next = applyDeltaToProfile(tasteProfile, delta);
    await db.upsertTasteProfile(userId, next);
    setTasteProfile(next);

    return ci;
  }, [userId, tasteProfile]);

  const dismissCheckInToday = useCallback(() => {
    try { localStorage.setItem(CHECK_IN_DISMISS_LS, new Date().toISOString()); } catch {}
  }, []);

  const checkInDismissedAt = useCallback(() => {
    try { return localStorage.getItem(CHECK_IN_DISMISS_LS); } catch { return null; }
  }, []);

  // ---- Shopping list ----
  const addShoppingItem = useCallback(async (text, opts = {}) => {
    if (!userId) throw new Error("Not signed in");
    // Merge-by-product: if a pending line for the same sku (or same text) already
    // exists, bump its quantity instead of adding a duplicate — a v1 requirement
    // now that several feeders (manual, recipe, depletion, toss) write here (§H).
    const dup = findDuplicateShoppingItem(shoppingListRef.current, { sku_id: opts.skuId || null, text });
    if (dup) {
      const nextQty = bumpedQuantity(dup.quantity, opts.quantity);
      if (nextQty === dup.quantity) return dup; // nothing to change
      const updated = await db.updateShoppingItem(dup.id, { quantity: nextQty });
      setShoppingList(prev => prev.map(i => i.id === dup.id ? updated : i));
      return updated;
    }
    const item = await db.addShoppingItem(userId, {
      text,
      sku_id: opts.skuId || null,
      quantity: opts.quantity || null,
      source: opts.source || "manual",
    });
    setShoppingList(prev => [item, ...prev]);
    return item;
  }, [userId]);

  const setShoppingDone = useCallback(async (itemId, done) => {
    const updated = await db.setShoppingItemDone(itemId, done);
    setShoppingList(prev => prev.map(i => i.id === itemId ? updated : i));
    return updated;
  }, []);

  const removeShoppingItem = useCallback(async (itemId) => {
    await db.deleteShoppingItem(itemId);
    setShoppingList(prev => prev.filter(i => i.id !== itemId));
  }, []);

  // Check off a shopping item AND bump pantry stock for pantry-linked items
  // (Phase 3: "checking off a shopping item bumps the linked item's stock").
  // Free-text items with no sku just get marked done. Returns { updated,
  // restocked } so the UI can confirm the stock bump.
  const completeShoppingItem = useCallback(async (item, { restock = true } = {}) => {
    const updated = await db.setShoppingItemDone(item.id, true);
    setShoppingList(prev => prev.map(i => i.id === item.id ? updated : i));
    let restocked = null;
    if (restock && item?.sku_id) {
      const sku = skus.find(s => s.id === item.sku_id);
      if (sku) {
        const qtyNum = parseFloat(String(item.quantity ?? "").replace(/[^0-9.]/g, "")) || 1;
        const entry = await db.addInventoryEntry(userId, newInventoryEntryForSku(sku, qtyNum));
        setInventory(prev => [...prev, entry]);
        restocked = { sku, entry };
      }
    }
    return { updated, restocked };
  }, [skus, userId]);

  // ---- Bottomless imports ----
  const acceptBottomlessImport = useCallback(async (importId) => {
    const imp = bottomlessImports.find(b => b.id === importId);
    if (!imp) return null;

    // Create a bean from the parsed payload
    const p = imp.parsed || {};
    const bean = await db.addBean(userId, {
      name: p.name || "Bottomless bag",
      roaster: p.roaster || null,
      origin: p.origin || [],
      roast_level: p.roast_level || "medium",
      process: p.process || "washed",
      weight_g: p.weight_g || null,
      weight_left_g: p.weight_g || null,
      tasting_notes: p.tasting_notes || [],
      details: { source: "bottomless", import_id: importId },
    });
    setBeans(prev => [bean, ...prev]);

    await db.updateBottomlessImport(importId, {
      status: "accepted",
      accepted_bean_id: bean.id,
    });
    setBottomlessImports(prev => prev.filter(b => b.id !== importId));
    return bean;
  }, [userId, bottomlessImports]);

  const dismissBottomlessImport = useCallback(async (importId) => {
    await db.updateBottomlessImport(importId, { status: "dismissed" });
    setBottomlessImports(prev => prev.filter(b => b.id !== importId));
  }, []);

  // ---- Pantry inbox (receipts) ----
  // Parse a queued receipt (email or photo) into structured items.
  // Uses the user's browser-side Anthropic key — no server-side AI billing.
  const parsePantryInboxItem = useCallback(async (inboxId) => {
    if (!apiKey) throw new Error("Add your AI provider key in Settings to parse receipts");
    const item = pantryInbox.find(p => p.id === inboxId);
    if (!item) throw new Error("Inbox item not found");

    let parsed;
    try {
      if (item.photo_path) {
        // We don't currently have a "fetch by storage path → File" helper.
        // Instead, parsing photo receipts is gated to upload-time (see addPantryReceiptPhoto).
        throw new Error("Photo already uploaded; re-upload to re-parse");
      } else if (item.raw_text || item.raw_html) {
        parsed = await parseReceiptFromText({
          apiKey,
          text: item.raw_text || stripHtml(item.raw_html || ""),
          subject: item.source_subject || "",
        });
      } else {
        throw new Error("No content to parse");
      }
    } catch (e) {
      const failed = await db.updatePantryInbox(inboxId, {
        status: "parse_failed",
        parse_error: e.message,
      });
      setPantryInbox(prev => prev.map(p => p.id === inboxId ? failed : p));
      throw e;
    }

    const updated = await db.updatePantryInbox(inboxId, {
      status: "parsed",
      parsed_items: parsed.items || [],
      source: parsed.source || item.source,
      source_order_id: parsed.order_id || item.source_order_id,
      source_total_cents: parsed.order_total_cents ?? item.source_total_cents,
      source_date: parsed.order_date || item.source_date,
      parse_error: null,
    });
    setPantryInbox(prev => prev.map(p => p.id === inboxId ? updated : p));
    return updated;
  }, [apiKey, pantryInbox]);

  // Upload a paper receipt photo (Trader Joe's etc.) and immediately parse.
  const addPantryReceiptPhoto = useCallback(async (file) => {
    if (!apiKey) throw new Error("Add your AI provider key in Settings");

    // Parse first (vision pass on the local file before uploading)
    const parsed = await parseReceiptFromPhoto({ apiKey, file });

    // Then upload the file for archival
    const photo_path = await uploadPhoto(file, "receipt", userId);

    const created = await db.addPantryInbox(userId, {
      source: parsed.source || "photo",
      source_subject: null,
      source_order_id: parsed.order_id || null,
      source_total_cents: parsed.order_total_cents ?? null,
      source_date: parsed.order_date || null,
      photo_path,
      parsed_items: parsed.items || [],
      status: "parsed",
    });
    setPantryInbox(prev => [created, ...prev]);
    return created;
  }, [apiKey, userId]);

  // Accept a parsed receipt: insert pantry rows for non-skipped items,
  // matching against existing SKUs where possible.
  const acceptPantryInboxItems = useCallback(async (inboxId, itemEdits = {}) => {
    const item = pantryInbox.find(p => p.id === inboxId);
    if (!item) throw new Error("Inbox item not found");

    const items = (item.parsed_items || [])
      .map((it, idx) => ({ ...it, ...(itemEdits[idx] || {}) }))
      .filter(it => !it.skip);

    if (!items.length) {
      throw new Error("Nothing to accept (all items marked skip)");
    }

    const { matched, new: fresh } = matchItemsToSkus(items, skus);
    const createdSkus = [];
    const createdEntries = [];

    // Increment matched SKUs (just add new inventory entries)
    for (const { item: parsedItem, sku } of matched) {
      const entry = await db.addInventoryEntry(userId, newInventoryEntryForSku(sku, parsedItem.quantity || 1));
      createdEntries.push(entry);
    }

    // Create new SKUs for unrecognized items
    for (const it of fresh) {
      const skuPayload = manualSku({
        name: it.name,
        category: it.category || "pantry",
      });
      const sku = await db.upsertSku(userId, skuPayload);
      createdSkus.push(sku);
      const entry = await db.addInventoryEntry(userId, newInventoryEntryForSku(sku, it.quantity || 1));
      createdEntries.push(entry);
    }

    // Update local state
    setSkus(prev => {
      const ids = new Set(prev.map(s => s.id));
      return [...prev, ...createdSkus.filter(s => !ids.has(s.id))];
    });
    setInventory(prev => [...prev, ...createdEntries]);

    // Mark the inbox item accepted
    const updated = await db.updatePantryInbox(inboxId, {
      status: "accepted",
      accepted_at: new Date().toISOString(),
    });
    setPantryInbox(prev => prev.map(p => p.id === inboxId ? updated : p));

    // The receipt photo isn't adopted by any created sku/entry — clean it up.
    cleanupInboxStorage(orphanedPaths(item.photo_path));

    return {
      skus_created: createdSkus.length,
      inventory_added: createdEntries.length,
      matched_count: matched.length,
    };
  }, [userId, pantryInbox, skus]);

  const dismissPantryInboxItem = useCallback(async (inboxId) => {
    const item = pantryInbox.find(p => p.id === inboxId);
    const updated = await db.updatePantryInbox(inboxId, { status: "dismissed" });
    setPantryInbox(prev => prev.map(p => p.id === inboxId ? updated : p));
    cleanupInboxStorage(item?.photo_path);
  }, [pantryInbox]);

  const deletePantryInboxItem = useCallback(async (inboxId) => {
    const item = pantryInbox.find(p => p.id === inboxId);
    await db.deletePantryInbox(inboxId);
    setPantryInbox(prev => prev.filter(p => p.id !== inboxId));
    cleanupInboxStorage(item?.photo_path);
  }, [pantryInbox]);

  // ---- Coffee inbox (shipment confirmations) ----
  const parseCoffeeInboxItem = useCallback(async (inboxId) => {
    if (!apiKey) throw new Error("Add your AI provider key in Settings to parse shipments");
    const item = coffeeInbox.find(c => c.id === inboxId);
    if (!item) throw new Error("Inbox item not found");

    let parsed;
    try {
      parsed = await parseCoffeeShipmentFromText({
        apiKey,
        text: item.raw_text || stripHtml(item.raw_html || ""),
        subject: item.source_subject || "",
      });
    } catch (e) {
      const failed = await db.updateCoffeeInbox(inboxId, {
        status: "parse_failed",
        parse_error: e.message,
      });
      setCoffeeInbox(prev => prev.map(c => c.id === inboxId ? failed : c));
      throw e;
    }

    const updated = await db.updateCoffeeInbox(inboxId, {
      status: "parsed",
      parsed,
      source: parsed.source || item.source,
      source_order_id: parsed.order_id || item.source_order_id,
      parse_error: null,
    });
    setCoffeeInbox(prev => prev.map(c => c.id === inboxId ? updated : c));
    return updated;
  }, [apiKey, coffeeInbox]);

  // Add a bag-photo to coffee inbox (e.g. you got a roaster's bag in the mail)
  const addCoffeeBagPhoto = useCallback(async (file) => {
    if (!apiKey) throw new Error("Add your AI provider key in Settings");

    const parsed = await parseCoffeeShipmentFromPhoto({ apiKey, file });
    const photo_path = await uploadPhoto(file, "coffee_bag", userId);

    const created = await db.addCoffeeInbox(userId, {
      source: parsed.source || "photo",
      photo_path,
      parsed,
      status: "parsed",
    });
    setCoffeeInbox(prev => [created, ...prev]);
    return created;
  }, [apiKey, userId]);

  // Accept a parsed coffee shipment: create bean rows for each bag.
  const acceptCoffeeInboxItem = useCallback(async (inboxId, bagIdx = 0) => {
    const item = coffeeInbox.find(c => c.id === inboxId);
    if (!item) throw new Error("Inbox item not found");
    const bags = item.parsed?.bags || [];
    if (!bags.length) {
      // Old Bottomless format: parsed is a single bag, not {bags: [...]}
      const p = item.parsed || {};
      if (p.name) bags.push(p);
    }
    const bag = bags[bagIdx];
    if (!bag) throw new Error(`No bag at index ${bagIdx}`);

    const bean = await db.addBean(userId, {
      name: bag.name || `${item.parsed?.roaster || "Coffee"} bag`,
      roaster: bag.roaster || item.parsed?.roaster || null,
      origin: bag.origin || [],
      roast_level: bag.roast_level || "medium",
      process: bag.process || "washed",
      roast_date: bag.roast_date || null,
      weight_g: bag.weight_g || null,
      weight_left_g: bag.weight_g || null,
      tasting_notes: bag.tasting_notes || [],
      details: { source: item.source, inbox_id: inboxId, price_cents: bag.price_cents },
    });
    setBeans(prev => [bean, ...prev]);

    const updated = await db.updateCoffeeInbox(inboxId, {
      status: "accepted",
      accepted_bean_id: bean.id,
      accepted_at: new Date().toISOString(),
    });
    setCoffeeInbox(prev => prev.map(c => c.id === inboxId ? updated : c));

    // The bag photo isn't adopted by the created bean — clean it up.
    cleanupInboxStorage(orphanedPaths(item.photo_path, bean.photo_path));

    return bean;
  }, [userId, coffeeInbox]);

  const dismissCoffeeInboxItem = useCallback(async (inboxId) => {
    const item = coffeeInbox.find(c => c.id === inboxId);
    const updated = await db.updateCoffeeInbox(inboxId, { status: "dismissed" });
    setCoffeeInbox(prev => prev.map(c => c.id === inboxId ? updated : c));
    cleanupInboxStorage(item?.photo_path);
  }, [coffeeInbox]);

  const deleteCoffeeInboxItem = useCallback(async (inboxId) => {
    const item = coffeeInbox.find(c => c.id === inboxId);
    await db.deleteCoffeeInbox(inboxId);
    setCoffeeInbox(prev => prev.filter(c => c.id !== inboxId));
    cleanupInboxStorage(item?.photo_path);
  }, [coffeeInbox]);

  // ---- Recipe inbox (forwarded recipe emails) ----
  // Parse a queued recipe email into structured form. If the email had image
  // attachments (downloaded by the webhook to Supabase Storage), fetch them
  // and run vision; otherwise try JSON-LD first (free, deterministic, covers
  // most cooking sites), falling back to a Claude text call.
  const parseRecipeInboxItem = useCallback(async (inboxId) => {
    const item = recipeInbox.find(r => r.id === inboxId);
    if (!item) throw new Error("Inbox item not found");

    // Fetch image attachments from Supabase Storage if present. Vision parsing
    // is gated on apiKey, so error early if photos are present and no key set.
    let images = [];
    const paths = item.attachment_paths || [];
    if (paths.length > 0) {
      if (!apiKey) {
        const failed = await db.updateRecipeInbox(inboxId, {
          status: "parse_failed",
          parse_error: "Add your AI provider key in Settings to parse photo recipes.",
        });
        setRecipeInbox(prev => prev.map(r => r.id === inboxId ? failed : r));
        throw new Error("An AI provider key is required for vision parsing");
      }
      try {
        images = await Promise.all(paths.map(loadStorageImage));
        images = images.filter(Boolean);
      } catch (e) {
        const failed = await db.updateRecipeInbox(inboxId, {
          status: "parse_failed",
          parse_error: `Couldn't load attached photos: ${e.message}`,
        });
        setRecipeInbox(prev => prev.map(r => r.id === inboxId ? failed : r));
        throw e;
      }
    }

    let parsed;
    try {
      parsed = await parseRecipeInbox({
        apiKey,
        text: item.text_body,
        html: item.html_body,
        subject: item.subject || "",
        images,
      });
    } catch (e) {
      const failed = await db.updateRecipeInbox(inboxId, {
        status: "parse_failed",
        parse_error: e.message,
      });
      setRecipeInbox(prev => prev.map(r => r.id === inboxId ? failed : r));
      throw e;
    }

    const updated = await db.updateRecipeInbox(inboxId, {
      status: "parsed",
      parsed,
      parsed_at: new Date().toISOString(),
      parse_error: null,
    });
    setRecipeInbox(prev => prev.map(r => r.id === inboxId ? updated : r));
    return updated;
  }, [apiKey, recipeInbox]);

  // Accept a parsed recipe inbox item: creates a new recipe row from the
  // parsed payload (with optional user edits) and links the inbox row to it.
  const acceptRecipeInboxItem = useCallback(async (inboxId, edits = {}) => {
    const item = recipeInbox.find(r => r.id === inboxId);
    if (!item) throw new Error("Inbox item not found");
    if (!item.parsed?.title) throw new Error("Item not parsed yet");

    // Merge edits over the parsed payload, then run through addRecipeRow's
    // flattenRecipe so the bulk-import shape lands as a recipes-table row.
    const merged = { ...item.parsed, ...edits };
    const created = await addRecipeRow(merged);

    const updated = await db.updateRecipeInbox(inboxId, {
      status: "imported",
      imported_at: new Date().toISOString(),
      imported_recipe_id: created.id,
    });
    setRecipeInbox(prev => prev.map(r => r.id === inboxId ? updated : r));

    // Attachments were vision inputs; delete any the imported recipe didn't keep
    // as its hero photo.
    cleanupInboxStorage(orphanedPaths(item.attachment_paths, created.photo_path));

    return { recipe: created, inbox: updated };
  }, [recipeInbox, addRecipeRow]);

  const dismissRecipeInboxItem = useCallback(async (inboxId) => {
    const item = recipeInbox.find(r => r.id === inboxId);
    const updated = await db.updateRecipeInbox(inboxId, { status: "dismissed" });
    setRecipeInbox(prev => prev.map(r => r.id === inboxId ? updated : r));
    cleanupInboxStorage(item?.attachment_paths);
  }, [recipeInbox]);

  const deleteRecipeInboxItem = useCallback(async (inboxId) => {
    const item = recipeInbox.find(r => r.id === inboxId);
    await db.deleteRecipeInbox(inboxId);
    setRecipeInbox(prev => prev.filter(r => r.id !== inboxId));
    cleanupInboxStorage(item?.attachment_paths);
  }, [recipeInbox]);

  // ---- Cook events (replaces simple markRecipeMade) ----
  const recordCookEvent = useCallback(async ({ recipeId, servingsMade, rating, notes, adjustments, decrementPantry = false }) => {
    if (!userId) throw new Error("Not signed in");
    const event = await db.addCookEvent(userId, {
      recipe_id: recipeId,
      servings_made: servingsMade,
      rating: rating || null,
      notes: notes || null,
      adjustments: adjustments || {},
      pantry_decremented: !!decrementPantry,
    });
    setCookEvents(prev => [event, ...prev]);

    // Increment recipe times_made
    const recipe = recipes.find(r => r.id === recipeId);
    if (recipe) {
      const updated = await db.updateRecipe(recipeId, {
        times_made: (recipe.times_made || 0) + 1,
        last_made_at: new Date().toISOString(),
      });
      setRecipes(prev => prev.map(r => r.id === recipeId ? updated : r));
    }

    // Fold the rating into the food taste profile (Phase 4 learning loop).
    // Mirrors submitCheckIn on the coffee side: rating + recipe tags → bias
    // delta → decayed running profile → persisted for the next suggestion.
    if (rating && recipe) {
      const delta = cookEventToTasteDelta(rating, recipe);
      if (delta) {
        const nextProfile = applyDeltaToFoodProfile(
          preferences.food_taste, delta, new Date().toISOString()
        );
        try {
          await db.upsertPreferences(userId, { food_taste: nextProfile });
          setPrefsState(prev => ({ ...prev, food_taste: nextProfile }));
        } catch (e) {
          // Learning is best-effort; never block logging a cook on it.
          console.warn("food taste profile update failed", e);
        }
      }
    }

    // Optionally decrement pantry inventory by recipe ingredient amounts
    if (decrementPantry && recipe) {
      await tryDecrementPantryFromRecipe(recipe, servingsMade);
    }

    return event;
  }, [userId, recipes, preferences]);

  const tryDecrementPantryFromRecipe = async (recipe, servingsMade) => {
    // Best-effort: match recipe ingredient names to existing skus by name.
    // This is intentionally fuzzy — if no match, skip silently.
    const scale = (servingsMade || recipe.yield_amount || 1) / (recipe.yield_amount || 1);
    for (const ing of recipe.ingredients || []) {
      const name = String(ing.name || "").toLowerCase().trim();
      if (!name) continue;
      const matchSku = skus.find(s => String(s.canonical_name || "").toLowerCase().includes(name));
      if (!matchSku) continue;
      // Find inventory entry for this sku, decrement by 1 unit (or amount * scale)
      const entry = inventory.find(i => i.sku_id === matchSku.id);
      if (!entry) continue;
      // Only count down to zero. We don't try to do real unit math here.
      const newQty = Math.max(0, (+entry.quantity || 0) - 1);
      if (newQty === 0) {
        try { await db.deleteInventoryEntry(entry.id); } catch {}
        setInventory(prev => prev.filter(i => i.id !== entry.id));
      }
    }
  };

  // ---- Preferences ----
  const setPreferences = useCallback(async (next) => {
    if (userId) {
      // Persist first; only mutate local state on success so UI rollback is
      // implicit on failure (caller catches and re-renders with prior values).
      const saved = await db.upsertPreferences(userId, next);
      setPrefsState({ ...DEFAULT_PREFS, ...(saved || next) });
      return saved;
    }
    setPrefsState(next);
    return next;
  }, [userId]);

  // Which bag is in the container. This is the ONE source of truth every coffee
  // surface reads via canisterBean(beans, preferences): the Coffee screen,
  // Morning Mode, the reorder card and the canister card. The Coffee screen used
  // to keep this in local component state, so a pick there never reached the
  // other cards and they drifted out of sync. Persisting last_bean_id keeps them
  // aligned. If the user maintains an explicit open-bags list, make sure the
  // chosen bag is on it, or canisterBean would fall back to the first open bag
  // and silently ignore the pick.
  const setContainerBean = useCallback(async (beanId) => {
    const prev = preferences || {};
    const patch = { last_bean_id: beanId };
    if (Array.isArray(prev.open_bean_ids) && beanId && !prev.open_bean_ids.includes(beanId)) {
      patch.open_bean_ids = [...prev.open_bean_ids, beanId];
    }
    return setPreferences({ ...prev, ...patch });
  }, [preferences, setPreferences]);

  return {
    // status
    userId, loading, error,
    // data
    appliances, skus, inventory, recipes, beans, brewLogs, preferences, apiKey,
    aiProvider, aiKeys, setAiProvider,
    gear,
    // learning loop data
    tasteProfile, favorites, checkIns, shoppingList, bottomlessImports, cookEvents,
    pantryInbox, coffeeInbox, recipeInbox,
    // morning mode
    morningSession,
    // derived
    ownedAppliances: appliances,
    ownedApplianceIds: appliances.map(a => a.library_id).filter(Boolean),
    ownedCapabilities,
    // helpers
    uploadEntityPhoto,
    // appliances
    addAppliance, updateApplianceRow, removeAppliance, toggleLibraryAppliance,
    // pantry
    scanBarcode, addManualPantryItem, removeInventoryEntry, updateInventoryEntry, useInventoryAmount, removeSku, setSkuMinKeep,
    commitResetItem,
    // recipes
    addRecipe: addRecipeRow,
    updateRecipe: updateRecipeRow,
    deleteRecipe: removeRecipe,
    createRecipeShareLink, revokeRecipeShareLink,
    markRecipeMade: markMade,   // legacy: just increments times_made
    recordCookEvent,             // new: full cook event with notes + decrement
    toggleFavorite,
    reorderFavoriteRecipes: reorderFavoriteRecipesList,
    bulkImportRecipes,
    // beans
    addBean: addBeanRow, updateBean: updateBeanRow, deleteBean: removeBean,
    reorderBeans: reorderBeansList,
    // coffee compute + log
    computeBrew, logBrew, logBlendBrew, updateBrewLog, deleteBrewLog,
    // ai usage
    getAiUsageSince: (sinceIso) => userId ? db.getAiUsageSince(userId, sinceIso) : Promise.resolve([]),
    // favorites
    saveFavorite, useFavorite, removeFavorite,
    // check-ins
    submitCheckIn, dismissCheckInToday, checkInDismissedAt,
    // morning mode
    startMorningSession, setMedsGiven, clearMedsGiven, setPrepStep, completeMorning,
    // shopping
    addShoppingItem, setShoppingDone, removeShoppingItem, completeShoppingItem,
    // bottomless
    acceptBottomlessImport, dismissBottomlessImport,
    // pantry inbox (receipts)
    parsePantryInboxItem, addPantryReceiptPhoto, acceptPantryInboxItems,
    dismissPantryInboxItem, deletePantryInboxItem,
    // coffee inbox (shipments)
    parseCoffeeInboxItem, addCoffeeBagPhoto, acceptCoffeeInboxItem,
    dismissCoffeeInboxItem, deleteCoffeeInboxItem,
    // recipe inbox (forwarded recipe emails)
    parseRecipeInboxItem, acceptRecipeInboxItem,
    dismissRecipeInboxItem, deleteRecipeInboxItem,
    // settings
    setApiKey, setPreferences, setContainerBean,
  };
}

// Normalize ingredient shape: bulk-import / vision payloads use {item, note},
// but the renderer + editor expect {name, preparation}. Pass-through anything
// already in the internal shape.
function normalizeIngredients(arr) {
  return (arr || []).map(i => {
    if (!i || typeof i !== "object") return i;
    const name = i.name ?? i.item ?? "";
    const preparation = i.preparation ?? i.note ?? "";
    const { item, note, ...rest } = i;
    return { ...rest, name, preparation };
  });
}

// Normalize step shape: bulk-import sends plain strings; renderer expects
// {instruction, title?} objects.
function normalizeSteps(arr) {
  return (arr || []).map(s => {
    if (typeof s === "string") return { instruction: s };
    if (s && typeof s === "object") {
      if (!s.instruction && typeof s.text === "string") {
        const { text, ...rest } = s;
        return { ...rest, instruction: text };
      }
      return s;
    }
    return s;
  });
}

function flattenRecipe(input) {
  // Accept either a nested {meta:{...}} shape (legacy) or a flat shape.
  if (input.meta) {
    const m = input.meta;
    return {
      title: input.title,
      summary: input.summary || "",
      source_type: input.source?.type || "manual",
      source_ref: input.source?.reference || null,
      source_author: input.source?.author || null,
      cuisine: m.cuisine || [],
      course: m.course || [],
      diet_tags: m.diet_tags || [],
      mood_tags: m.mood_tags || [],
      yield_amount: m.yield?.amount ?? 4,
      yield_unit: m.yield?.unit || "servings",
      prep_min: m.time?.prep_min || 0,
      active_min: m.time?.active_min || 0,
      passive_min: m.time?.passive_min || 0,
      total_min: m.time?.total_min || 0,
      ingredients: normalizeIngredients(input.ingredients),
      steps: normalizeSteps(input.steps),
      // `equipment` is the canonical field. Unioning legacy `cookware` here
      // means import paths that wrote one or the other land consistently.
      equipment: unionAppliances(input.equipment, input.cookware),
      notes: input.notes || "",
      photo_path: input.photo_path || null,
      source_image: input.source_image || input._source_image || null,
    };
  }
  // Flat shape: strip leading-underscore keys (Supabase rejects unknown columns)
  // and map the documented `_source_image` field to its real `source_image` column.
  const out = {};
  for (const [k, v] of Object.entries(input)) {
    if (!k.startsWith("_")) out[k] = v;
  }
  if (input._source_image && !out.source_image) out.source_image = input._source_image;
  if (out.ingredients) out.ingredients = normalizeIngredients(out.ingredients);
  if (out.steps) out.steps = normalizeSteps(out.steps);
  // Same canonical-equipment merge as the nested branch.
  if (out.equipment !== undefined || out.cookware !== undefined) {
    out.equipment = unionAppliances(out.equipment, out.cookware);
  }
  return out;
}

// Union two appliance-id arrays into a deduped, ordered list. Used to
// collapse the historical cookware/equipment split into a single canonical
// `equipment` field on writes.
function unionAppliances(a, b) {
  const seen = new Set();
  const out = [];
  for (const arr of [a, b]) {
    for (const v of arr || []) {
      if (!v) continue;
      const key = String(v);
      if (seen.has(key)) continue;
      seen.add(key); out.push(key);
    }
  }
  return out;
}

function normalizeTitle(t) {
  return String(t || "").toLowerCase().replace(/[^a-z0-9]/g, "").trim();
}

function dedupTags(arr) {
  return [...new Set((arr || []).filter(Boolean).map(t => String(t).toLowerCase()))];
}

function stripHtml(html) {
  if (!html) return "";
  // Remove script/style content, then tags, then collapse whitespace
  return String(html)
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\s+/g, " ")
    .trim();
}

// Fetch a Supabase Storage path as { base64, mediaType } for vision parsing.
// Uses a signed URL because direct browser-side downloads via supabase-js
// require an extra round-trip; signed URLs cache cleanly via the browser.
async function loadStorageImage(path) {
  const url = await getPhotoUrl(path, 3600);
  if (!url) throw new Error(`No signed URL for ${path}`);
  const r = await fetch(url);
  if (!r.ok) throw new Error(`Image fetch ${r.status} for ${path}`);
  const blob = await r.blob();
  const base64 = await blobToBase64(blob);
  return { base64, mediaType: blob.type || "image/jpeg" };
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const comma = typeof result === "string" ? result.indexOf(",") : -1;
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
